import React, { useState, useMemo, useEffect } from 'react';
import {
  Plus,
  Search,
  Trash2,
  Copy,
  TestTube,
  Globe,
  Key,
  Cpu,
  Download,
  Upload,
  RefreshCw,
  Zap,
  AlertCircle,
  CheckCircle,
  XCircle,
  Filter,
  MoreVertical,
  ChevronDown,
  Eye,
  EyeOff,
  Clock,
  Activity,
  Server,
  Shield,
  Wifi,
  Cloud,
  AlertTriangle,
  Star,
  Settings,
  Edit3,
  ExternalLink,
  List,
  Grid,
  Menu,
  X,
  TrendingUp,
  TrendingDown,
  Info,
} from 'lucide-react';
import { useEnhancedAppStore } from '@/stores/EnhancedAppStore';
import { ApiProvider, ProviderType } from '@/types';
import { ApiService, ApiTestResult } from '@/services/ApiService';
import clsx from 'clsx';

const PROVIDER_TYPE_CONFIG: Record<ProviderType, {
  name: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon: React.ReactNode;
  description: string;
}> = {
  [ProviderType.OPENAI]: {
    name: 'OpenAI',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    icon: <Zap className="w-4 h-4" />,
    description: 'OpenAI 兼容 API',
  },
  [ProviderType.ANTHROPIC]: {
    name: 'Anthropic',
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    icon: <Cpu className="w-4 h-4" />,
    description: 'Claude 模型 API',
  },
  [ProviderType.GOOGLE_AI]: {
    name: 'Google AI',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    icon: <Cloud className="w-4 h-4" />,
    description: 'Gemini 模型 API',
  },
  [ProviderType.AZURE_OPENAI]: {
    name: 'Azure OpenAI',
    color: 'text-cyan-700',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
    icon: <Server className="w-4 h-4" />,
    description: 'Azure OpenAI 服务',
  },
  [ProviderType.CUSTOM]: {
    name: '自定义 API',
    color: 'text-gray-700',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    icon: <Globe className="w-4 h-4" />,
    description: '自定义 API 端点',
  },
};

const STATUS_ICONS = {
  online: <CheckCircle className="w-4 h-4 text-green-500" />,
  offline: <XCircle className="w-4 h-4 text-red-500" />,
  testing: <RefreshCw className="w-4 h-4 text-yellow-500 animate-spin" />,
  warning: <AlertTriangle className="w-4 h-4 text-yellow-500" />,
  unknown: <HelpCircle className="w-4 h-4 text-gray-400" />,
};

const getBatteryIcon = (level: number) => {
  if (level >= 80) return <BatteryFull className="w-4 h-4 text-green-500" />;
  if (level >= 60) return <BatteryCharging className="w-4 h-4 text-green-400" />;
  if (level >= 40) return <BatteryMedium className="w-4 h-4 text-yellow-500" />;
  if (level >= 20) return <BatteryLow className="w-4 h-4 text-orange-500" />;
  return <Battery className="w-4 h-4 text-red-500" />;
};

const getConnectionIcon = (latency?: number) => {
  if (!latency) return <WifiOff className="w-4 h-4 text-gray-400" />;
  if (latency < 100) return <Wifi className="w-4 h-4 text-green-500" />;
  if (latency < 300) return <Wifi className="w-4 h-4 text-yellow-500" />;
  return <Wifi className="w-4 h-4 text-red-500" />;
};

interface AddProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (provider: Omit<ApiProvider, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
}

const AddProviderModal: React.FC<AddProviderModalProps> = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    name: '',
    baseUrl: '',
    apiKey: '',
    providerType: ProviderType.CUSTOM as ProviderType,
  });
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<ApiTestResult | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  if (!isOpen) return null;

  const handleTest = async () => {
    if (!formData.baseUrl || !formData.apiKey) return;
    
    setIsTesting(true);
    try {
      const result = await ApiService.testConnection({
        baseUrl: formData.baseUrl,
        apiKey: formData.apiKey,
        providerType: formData.providerType,
      });
      setTestResult(result);
    } catch (error) {
      setTestResult({
        success: false,
        error: error instanceof Error ? error.message : '测试失败',
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.baseUrl || !formData.apiKey) {
      alert('请填写所有必填字段');
      return;
    }

    await onAdd({
      name: formData.name,
      baseUrl: formData.baseUrl,
      apiKey: formData.apiKey,
      providerType: formData.providerType,
      models: testResult?.models || [],
      defaultModel: undefined,
      config: {
        temperature: 0.7,
        maxTokens: 1000,
      },
    });
    
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">添加供应商</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            {/* 名称 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                供应商名称 *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input w-full"
                placeholder="例如: My OpenAI API"
              />
            </div>

            {/* 供应商类型 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                供应商类型
              </label>
              <div className="grid grid-cols-5 gap-2">
                {Object.entries(PROVIDER_TYPE_CONFIG).map(([type, config]) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setFormData({ ...formData, providerType: type as ProviderType })}
                    className={clsx(
                      'p-3 rounded-lg border flex flex-col items-center gap-2',
                      formData.providerType === type
                        ? config.borderColor + ' ' + config.bgColor
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <div className={clsx('p-2 rounded-full', config.bgColor)}>
                      {config.icon}
                    </div>
                    <div className="text-xs font-medium">{config.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* API 端点 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API 端点 URL *
              </label>
              <input
                type="url"
                value={formData.baseUrl}
                onChange={(e) => setFormData({ ...formData, baseUrl: e.target.value })}
                className="input w-full font-mono"
                placeholder="https://api.openai.com/v1"
              />
            </div>

            {/* API 密钥 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API 密钥 *
              </label>
              <div className="relative">
                <input
                  type={showApiKey ? "text" : "password"}
                  value={formData.apiKey}
                  onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                  className="input w-full font-mono pr-10"
                  placeholder="sk-..."
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* 测试按钮和结果 */}
            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-medium text-gray-900">连接测试</div>
                  <div className="text-sm text-gray-500">验证配置是否正确</div>
                </div>
                <button
                  onClick={handleTest}
                  disabled={!formData.baseUrl || !formData.apiKey || isTesting}
                  className={clsx(
                    'btn-primary',
                    (!formData.baseUrl || !formData.apiKey || isTesting) && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {isTesting ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      测试中...
                    </>
                  ) : (
                    <>
                      <TestTube className="w-4 h-4 mr-2" />
                      测试连接
                    </>
                  )}
                </button>
              </div>

              {testResult && (
                <div className={clsx(
                  'p-4 rounded-lg',
                  testResult.success
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-red-50 border border-red-200'
                )}>
                  <div className="flex items-center gap-2 mb-2">
                    {testResult.success ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    <div className="font-medium">
                      {testResult.success ? '连接成功' : '连接失败'}
                    </div>
                  </div>
                  
                  {testResult.latency && (
                    <div className="text-sm text-gray-600 mb-2">
                      延迟: <span className="font-mono">{testResult.latency}ms</span>
                    </div>
                  )}
                  
                  {testResult.error && (
                    <div className="text-sm text-red-600">{testResult.error}</div>
                  )}
                  
                  {testResult.models && testResult.models.length > 0 && (
                    <div className="mt-3">
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        检测到 {testResult.models.length} 个模型
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {testResult.models.slice(0, 5).map((model, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-white border border-gray-200 rounded text-xs font-mono"
                          >
                            {model}
                          </span>
                        ))}
                        {testResult.models.length > 5 && (
                          <span className="px-2 py-1 text-gray-500 text-xs">
                            +{testResult.models.length - 5} 更多
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-6 border-t border-gray-200 mt-6">
            <button
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              取消
            </button>
            <button
              onClick={handleSubmit}
              disabled={!formData.name || !formData.baseUrl || !formData.apiKey}
              className={clsx(
                'btn-primary flex-1',
                (!formData.name || !formData.baseUrl || !formData.apiKey) && 'opacity-50 cursor-not-allowed'
              )}
            >
              添加供应商
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const EnhancedProviderManagerPage: React.FC = () => {
  const {
    providers,
    deleteProvider,
    setCurrentProvider,
    testProviderConnection,
    batchTestProviders,
    testingProviders,
    providerStats,
    refreshProviderModels,
    setDefaultModel,
    updateProviderWithTest,
  } = useEnhancedAppStore();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<ProviderType | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'online' | 'offline'>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'name' | 'lastTested' | 'latency'>('name');
  const [expandedProvider, setExpandedProvider] = useState<string | null>(null);
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());

  // 过滤和排序供应商
  const filteredProviders = useMemo(() => {
    let filtered = providers.filter(provider => {
      // 搜索过滤
      const matchesSearch = searchQuery === '' || 
        provider.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        provider.baseUrl.toLowerCase().includes(searchQuery.toLowerCase());
      
      // 类型过滤
      const matchesType = filterType === 'all' || provider.providerType === filterType;
      
      // 状态过滤
      const matchesStatus = filterStatus === 'all' || 
        (filterStatus === 'online' && providerStats[provider.id]?.lastSuccess) ||
        (filterStatus === 'offline' && providerStats[provider.id]?.lastSuccess === false);
      
      return matchesSearch && matchesType && matchesStatus;
    });

    // 排序
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'lastTested': {
          const aTime = providerStats[a.id]?.lastTested || '';
          const bTime = providerStats[b.id]?.lastTested || '';
          return bTime.localeCompare(aTime);
        }
        case 'latency': {
          const aLatency = providerStats[a.id]?.averageLatency || Infinity;
          const bLatency = providerStats[b.id]?.averageLatency || Infinity;
          return aLatency - bLatency;
        }
        default:
          return 0;
      }
    });

    return filtered;
  }, [providers, searchQuery, filterType, filterStatus, sortBy, providerStats]);

  // 获取供应商状态
  const getProviderStatus = (providerId: string) => {
    if (testingProviders[providerId]) return 'testing';
    
    const stats = providerStats[providerId];
    if (!stats || stats.testCount === 0) return 'unknown';
    
    if (stats.lastSuccess === true) {
      const successRate = (stats.successCount / stats.testCount) * 100;
      if (successRate >= 90) return 'online';
      if (successRate >= 70) return 'warning';
      return 'offline';
    }
    
    return 'offline';
  };

  // 处理删除供应商
  const handleDeleteProvider = (id: string, name: string) => {
    if (confirm(`确定要删除供应商 "${name}" 吗？此操作不可撤销。`)) {
      deleteProvider(id);
    }
  };

  // 处理测试供应商
  const handleTestProvider = async (id: string) => {
    await testProviderConnection(id);
  };

  // 批量测试
  const handleBatchTest = async () => {
    if (bulkSelected.size === 0) {
      await batchTestProviders();
    } else {
      await batchTestProviders(Array.from(bulkSelected));
    }
  };

  // 刷新模型列表
  const handleRefreshModels = async (id: string) => {
    try {
      const models = await refreshProviderModels(id);
      alert(`✅ 成功刷新模型列表，共 ${models.length} 个模型`);
    } catch (error) {
      alert('❌ 刷新失败: ' + (error instanceof Error ? error.message : '未知错误'));
    }
  };

  // 切换批量选择
  const toggleBulkSelect = (id: string) => {
    const newSelected = new Set(bulkSelected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setBulkSelected(newSelected);
  };

  // 选择所有
  const selectAll = () => {
    if (bulkSelected.size === filteredProviders.length) {
      setBulkSelected(new Set());
    } else {
      setBulkSelected(new Set(filteredProviders.map(p => p.id)));
    }
  };

  // 添加供应商
  const handleAddProvider = async (providerData: Omit<ApiProvider, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      await updateProviderWithTest(
        `temp_${Date.now()}`,
        providerData,
        true
      );
      alert('✅ 供应商添加成功！');
    } catch (error) {
      alert('❌ 添加失败: ' + (error instanceof Error ? error.message : '未知错误'));
    }
  };

  // 统计信息
  const stats = useMemo(() => {
    const total = providers.length;
    const online = providers.filter(p => getProviderStatus(p.id) === 'online').length;
    const offline = providers.filter(p => getProviderStatus(p.id) === 'offline').length;
    const testing = providers.filter(p => testingProviders[p.id]).length;
    const avgLatency = Object.values(providerStats)
      .filter(s => s.averageLatency)
      .reduce((sum, s) => sum + (s.averageLatency || 0), 0) / 
      Object.values(providerStats).filter(s => s.averageLatency).length || 0;
    
    return { total, online, offline, testing, avgLatency };
  }, [providers, providerStats, testingProviders]);

  return (
    <div className="space-y-6">
      {/* 头部统计 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-500">总供应商</div>
            </div>
            <div className="p-3 bg-primary-100 rounded-lg">
              <Server className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-green-600">{stats.online}</div>
              <div className="text-sm text-gray-500">在线</div>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Activity className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-red-600">{stats.offline}</div>
              <div className="text-sm text-gray-500">离线</div>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <CloudOff className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {Math.round(stats.avgLatency)}ms
              </div>
              <div className="text-sm text-gray-500">平均延迟</div>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Network className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* 操作栏 */}
      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10 w-full"
                placeholder="搜索供应商..."
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as ProviderType | 'all')}
                className="input"
              >
                <option value="all">所有类型</option>
                {Object.entries(PROVIDER_TYPE_CONFIG).map(([type, config]) => (
                  <option key={type} value={type}>
                    {config.name}
                  </option>
                ))}
              </select>
              
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as 'all' | 'online' | 'offline')}
                className="input"
              >
                <option value="all">所有状态</option>
                <option value="online">在线</option>
                <option value="offline">离线</option>
              </select>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={clsx(
                  'p-2 rounded-lg',
                  viewMode === 'grid'
                    ? 'bg-primary-100 text-primary-600'
                    : 'text-gray-400 hover:text-gray-600'
                )}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={clsx(
                  'p-2 rounded-lg',
                  viewMode === 'list'
                    ? 'bg-primary-100 text-primary-600'
                    : 'text-gray-400 hover:text-gray-600'
                )}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'lastTested' | 'latency')}
              className="input"
            >
              <option value="name">按名称排序</option>
              <option value="lastTested">按最后测试时间</option>
              <option value="latency">按延迟排序</option>
            </select>
            
            <button
              onClick={handleBatchTest}
              disabled={filteredProviders.length === 0}
              className="btn-primary"
            >
              <TestTube className="w-4 h-4 mr-2" />
              批量测试
            </button>
            
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-accent"
            >
              <Plus className="w-4 h-4 mr-2" />
              添加供应商
            </button>
          </div>
        </div>
      </div>

      {/* 批量操作栏 */}
      {bulkSelected.size > 0 && (
        <div className="card bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="font-medium text-blue-800">
                已选择 {bulkSelected.size} 个供应商
              </div>
              <button
                onClick={selectAll}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {bulkSelected.size === filteredProviders.length ? '取消全选' : '全选'}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleBatchTest}
                className="btn-primary"
              >
                <TestTube className="w-4 h-4 mr-2" />
                测试选中
              </button>
              <button
                onClick={() => setBulkSelected(new Set())}
                className="btn-secondary"
              >
                取消选择
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 供应商列表/网格 */}
      {filteredProviders.length === 0 ? (
        <div className="card">
          <div className="text-center py-12">
            <Server className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">暂无供应商</h3>
            <p className="text-gray-500 mb-6">
              {searchQuery || filterType !== 'all' || filterStatus !== 'all'
                ? '没有找到匹配的供应商，请调整筛选条件'
                : '您还没有添加任何 API 供应商'}
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-accent"
            >
              <Plus className="w-4 h-4 mr-2" />
              添加第一个供应商
            </button>
          </div>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProviders.map((provider) => {
            const status = getProviderStatus(provider.id);
            const stats = providerStats[provider.id];
            const config = PROVIDER_TYPE_CONFIG[provider.providerType];
            const isExpanded = expandedProvider === provider.id;
            const isSelected = bulkSelected.has(provider.id);
            const isTesting = testingProviders[provider.id];
            
            return (
              <div
                key={provider.id}
                className={clsx(
                  'card group relative',
                  isSelected && 'ring-2 ring-primary-500 ring-offset-2',
                  isTesting && 'opacity-75'
                )}
              >
                {/* 选择框 */}
                <div className="absolute top-3 left-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleBulkSelect(provider.id)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </div>
                
                {/* 供应商头部 */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={clsx('p-2 rounded-lg', config.bgColor)}>
                      {config.icon}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{provider.name}</div>
                      <div className="text-sm text-gray-500">{config.description}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {STATUS_ICONS[status]}
                    <button
                      onClick={() => setExpandedProvider(isExpanded ? null : provider.id)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <ChevronDown className={clsx('w-5 h-5 transition-transform', isExpanded && 'rotate-180')} />
                    </button>
                  </div>
                </div>
                
                {/* 基本信息 */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <div className="text-gray-500">API 端点</div>
                    <div className="font-mono truncate max-w-[200px]" title={provider.baseUrl}>
                      {new URL(provider.baseUrl).hostname}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <div className="text-gray-500">模型数量</div>
                    <div className="font-medium">{provider.models.length}</div>
                  </div>
                  
                  {stats && (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <div className="text-gray-500">最后测试</div>
                        <div className="flex items-center gap-1">
                          {stats.lastSuccess ? (
                            <CheckCircle className="w-3 h-3 text-green-500" />
                          ) : (
                            <XCircle className="w-3 h-3 text-red-500" />
                          )}
                          <div>
                            {stats.lastTested ? new Date(stats.lastTested).toLocaleTimeString() : '从未'}
                          </div>
                        </div>
                      </div>
                      
                      {stats.averageLatency && (
                        <div className="flex items-center justify-between text-sm">
                          <div className="text-gray-500">平均延迟</div>
                          <div className="flex items-center gap-1">
                            {getConnectionIcon(stats.averageLatency)}
                            <div>{stats.averageLatency}ms</div>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between text-sm">
                        <div className="text-gray-500">成功率</div>
                        <div className="flex items-center gap-1">
                          {getBatteryIcon((stats.successCount / stats.testCount) * 100)}
                          <div>{Math.round((stats.successCount / stats.testCount) * 100)}%</div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                
                {/* 展开的详细信息 */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                    {/* 模型列表 */}
                    {provider.models.length > 0 && (
                      <div>
                        <div className="text-sm font-medium text-gray-700 mb-2">可用模型</div>
                        <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                          {provider.models.map((model, index) => (
                            <span
                              key={index}
                              className={clsx(
                                'px-2 py-1 rounded text-xs font-mono',
                                model === provider.defaultModel
                                  ? 'bg-primary-100 text-primary-800 border border-primary-200'
                                  : 'bg-gray-100 text-gray-800 border border-gray-200'
                              )}
                              title={model}
                            >
                              {model}
                              {model === provider.defaultModel && (
                                <Star className="w-3 h-3 ml-1 inline" />
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* 操作按钮 */}
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleTestProvider(provider.id)}
                        disabled={isTesting}
                        className="btn-primary text-sm"
                      >
                        {isTesting ? (
                          <>
                            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                            测试中...
                          </>
                        ) : (
                          <>
                            <TestTube className="w-3 h-3 mr-1" />
                            测试连接
                          </>
                        )}
                      </button>
                      
                      <button
                        onClick={() => handleRefreshModels(provider.id)}
                        className="btn-secondary text-sm"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        刷新模型
                      </button>
                      
                      <button
                        onClick={() => setCurrentProvider(provider.id)}
                        className="btn-accent text-sm"
                      >
                        <Play className="w-3 h-3 mr-1" />
                        使用
                      </button>
                      
                      <button
                        onClick={() => handleDeleteProvider(provider.id, provider.name)}
                        className="btn-danger text-sm"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        删除
                      </button>
                    </div>
                  </div>
                )}
                
                {/* 底部操作栏 */}
                {!isExpanded && (
                  <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between">
                    <button
                      onClick={() => handleTestProvider(provider.id)}
                      disabled={isTesting}
                      className="text-sm text-primary-600 hover:text-primary-800 flex items-center gap-1"
                    >
                      {isTesting ? (
                        <>
                          <RefreshCw className="w-3 h-3 animate-spin" />
                          测试中
                        </>
                      ) : (
                        <>
                          <TestTube className="w-3 h-3" />
                          测试
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => setCurrentProvider(provider.id)}
                      className="text-sm text-accent-600 hover:text-accent-800 flex items-center gap-1"
                    >
                      <Play className="w-3 h-3" />
                      使用
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        // 列表视图
        <div className="card p-0 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={bulkSelected.size === filteredProviders.length && filteredProviders.length > 0}
                    onChange={selectAll}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  供应商
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  状态
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  API 端点
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  模型
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  延迟
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  最后测试
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredProviders.map((provider) => {
                const status = getProviderStatus(provider.id);
                const stats = providerStats[provider.id];
                const config = PROVIDER_TYPE_CONFIG[provider.providerType];
                const isTesting = testingProviders[provider.id];
                const isSelected = bulkSelected.has(provider.id);
                
                return (
                  <tr key={provider.id} className={clsx(isTesting && 'opacity-75')}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleBulkSelect(provider.id)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={clsx('p-2 rounded-lg mr-3', config.bgColor)}>
                          {config.icon}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{provider.name}</div>
                          <div className="text-sm text-gray-500">{config.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {STATUS_ICONS[status]}
                        <span className={clsx(
                          'text-sm font-medium',
                          status === 'online' && 'text-green-600',
                          status === 'offline' && 'text-red-600',
                          status === 'warning' && 'text-yellow-600',
                          status === 'testing' && 'text-blue-600',
                          status === 'unknown' && 'text-gray-600'
                        )}>
                          {status === 'online' && '在线'}
                          {status === 'offline' && '离线'}
                          {status === 'warning' && '警告'}
                          {status === 'testing' && '测试中'}
                          {status === 'unknown' && '未知'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-mono text-sm" title={provider.baseUrl}>
                        {new URL(provider.baseUrl).hostname}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-900">{provider.models.length}</span>
                        {provider.defaultModel && (
                          <span className="px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full">
                            默认: {provider.defaultModel}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {stats?.averageLatency ? (
                          <>
                            {getConnectionIcon(stats.averageLatency)}
                            <span className="text-sm text-gray-900">{stats.averageLatency}ms</span>
                          </>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {stats?.lastTested ? (
                          new Date(stats.lastTested).toLocaleString()
                        ) : (
                          <span className="text-gray-400">从未</span>
                        )}
                      </div>
                      {stats && (
                        <div className="text-xs text-gray-500">
                          成功率: {Math.round((stats.successCount / stats.testCount) * 100)}%
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleTestProvider(provider.id)}
                          disabled={isTesting}
                          className={clsx(
                            'p-2 rounded-lg',
                            isTesting
                              ? 'bg-blue-100 text-blue-600'
                              : 'bg-green-100 text-green-600 hover:bg-green-200'
                          )}
                          title="测试连接"
                        >
                          {isTesting ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <TestTube className="w-4 h-4" />
                          )}
                        </button>
                        
                        <button
                          onClick={() => handleRefreshModels(provider.id)}
                          className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200"
                          title="刷新模型"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        
                        <button
                          onClick={() => setCurrentProvider(provider.id)}
                          className="p-2 rounded-lg bg-accent-100 text-accent-600 hover:bg-accent-200"
                          title="使用此供应商"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        
                        <button
                          onClick={() => handleDeleteProvider(provider.id, provider.name)}
                          className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
                          title="删除供应商"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 添加供应商模态框 */}
      <AddProviderModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddProvider}
      />
    </div>
  );
};